import {
    IEditorViewableServiceLayer,
} from '@src/slide/application/entry/toc/editor/base/service-layer/viewable-service-layer.interface';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import { BaseViewableServiceLayer } from '@src/slide/application/entry/toc/base/service-layer/base-viewable-service-layer';
import { isRemotePlayerTest } from '@src/slide/infrastructure/module/dependent/runtime-environment/remote-player-test-env';
import CanvasE2EManager from '@src/js/auto-test/canvas-e2e';
import ENV from '@src/slide/infrastructure/module/dependent/runtime-environment/enviroment';
import OpenClientApiManager from '@src/slide/application/service/open-client-api-manager';
import ExportImageManager from '@src/slide/application/service/export-image-manager';
import { DynamicServiceLoader, IDynamicService } from '@src/slide/infrastructure/module/independent/dynamic-loader/dynamic-service';
import { exportManagerDynamicConfig } from './service-dynamic-configs/export-manager.config';
import { IExportManager } from '@src/slide/application/service/export-manager/core/interface';
import { UnsavedChangesManager } from '@src/slide/application/service/unsaved-changes-manager/core';
import { ICursorManager } from '@src/slide/application/service/cursor-manager/core/cursor-manager.interface';
import { IUpdateNoticeManager } from '@src/slide/application/service/update-notice-manager/core/update-notice-manager.interface';
import UpdateNoticeManager from '@src/slide/application/service/update-notice-manager/core';
import { IUrlParamManager } from '@src/slide/application/service/url-param-manager/core/url-param-manager.interface';
import { urlParamManagerDynamicConfig } from './service-dynamic-configs/url-param-manager.config';
import PsKeyManager from '@src/slide/application/service/pskey-manager/core';
import { PsKeyManagerInjectable } from '@src/slide/application/service/pskey-manager/inject/pskey-manager-injectable';
import { getCursorManagerDynamicConfig } from '@src/slide/application/entry/toc/base/service-layer/service-dynamic-configs/cursor-manager.config';
import VipInfoService from '@src/slide/application/service/vip-info-service';
import { IBaseCanvasService } from '@src/slide/application/service/canvas-component/core/base/type';
import { IProduceBackupManager } from '@src/slide/application/service/produce-backup-manager/core/produce-backup-manager.interface';
import { ISwitchPageMonitor } from '@src/slide/application/service/switch-page-monitor/core/switch-page-monitor.interface';
import SwitchPageMonitor from '@src/slide/application/service/switch-page-monitor/core';
import type RegisterManager from '@src/slide/application/service/register-manager/core/pc';
import { IBaseNoteAreaService } from '@src/slide/application/service/note-area-component/core/base/base-note-area-service.interface';
import { PrintService } from '@src/slide/application/service/print-service/core';
import PrintServiceInjectable from '@src/slide/application/service/print-service/inject/toc';
import { IInfrastructureBase } from '@src/slide/infrastructure/entry/base/infrastructure-base.interface';
import { IWorkbenchService } from '@src/slide/application/service/workbench-service/core/workbench-service.interface';
import { IAnonymousViewService } from '@src/slide/application/service/anonymous-view-service';
import { anonymousViewConfig } from './service-dynamic-configs/anonymous-view.config';
import { IBaseEditorExtensionService } from '@src/slide/application/service/edit-extension/core/editor/base/interface';
import { IApplyAuthService } from '@src/slide/application/service/apply-auth-service/core/interface';
import ApplyAuthService from '@src/slide/application/service/apply-auth-service/core';
import { IApplyAuthInjectable } from '@src/slide/application/service/apply-auth-service/inject/interface';
import ApplyAuthInjectable from '@src/slide/application/service/apply-auth-service/inject/toc';
import { IPrintServiceInjectable } from '@src/slide/application/service/print-service/core/interface';
import { isPreview } from '@src/slide/infrastructure/module/dependent/runtime-environment/drive-env';
import ScreenshotManager from '@src/slide/application/service/screenshot';
import { IScreenshotManager } from '@src/slide/application/service/screenshot/screenshot.interface';

export abstract class EditorViewableServiceLayer
    extends BaseViewableServiceLayer
    implements IEditorViewableServiceLayer {
    public switchPageMonitor: ISwitchPageMonitor;
    public readonly openClientApiManager;
    public readonly exportImageManager: ExportImageManager;
    public readonly exportManager: IDynamicService<IExportManager>;
    public canvasService: IBaseCanvasService;
    declare public extensionService: IBaseEditorExtensionService;
    public readonly noteAreaService: IDynamicService<IBaseNoteAreaService>;
    public readonly cursorManager: IDynamicService<ICursorManager>;
    public readonly updateNoticeManager: IUpdateNoticeManager;
    public readonly urlParamManager: IDynamicService<IUrlParamManager>;
    public readonly psKeyManager;
    public readonly applyAuthService: IApplyAuthService;
    public workbenchService: IDynamicService<IWorkbenchService>;
    public readonly vipInfoService: VipInfoService;
    public readonly produceBackupManager: IDynamicService<IProduceBackupManager>;
    public readonly printService: PrintService;
    public readonly anonymousViewService: IDynamicService<IAnonymousViewService>;
    public readonly screenshotManager: IScreenshotManager;
    protected registerManager: RegisterManager;

    public constructor(uiDataService: IUiDataService, infrastructure: IInfrastructureBase) {
        super(uiDataService, infrastructure);
        this.initRegisterManager();
        if (isRemotePlayerTest() || __IS_DEV__ || ENV.IS_TEST) {
            CanvasE2EManager.getInstance().setServiceDelegate({
                rendererService: this.contentRendererService,
                selection: this.selectionService,
            });
        }
        this.psKeyManager = new PsKeyManager(new PsKeyManagerInjectable());
        new UnsavedChangesManager(this.selectionService);
        uiDataService.setSlideRenderStateManagerDelegate(this.slideRenderStateManager);
        this.switchPageMonitor = new SwitchPageMonitor();
        this.switchPageMonitor.setSelectionServiceDelegate(this.selectionService);
        this.exportImageManager = new ExportImageManager(this.slideRenderStateManager);
        this.exportImageManager.setSelectionService(this.selectionService);
        this.exportImageManager.setUiDataService(uiDataService);
        this.exportImageManager.setWatermarkManager(this.watermarkManager);
        // 初始化 opensdk 服务
        if (this.shouldInitOpenClientApi()) {
            this.openClientApiManager = new OpenClientApiManager(
                this.playManager,
                this.selectionService,
                this.editAreaCanvasSizeManager,
                this.exportImageManager,
            );
        }
        this.applyAuthService = new ApplyAuthService(this.createApplyAuthInjectable());
        this.exportManager = DynamicServiceLoader.getInstance()
            .registerDynamicService(exportManagerDynamicConfig);
        this.cursorManager = DynamicServiceLoader.getInstance()
            .registerDynamicService(getCursorManagerDynamicConfig(
                uiDataService,
                this.selectionService,
            ));
        this.selectionEventService.setCursorManagerDelegate(this.cursorManager);
        this.updateNoticeManager = new UpdateNoticeManager();
        this.urlParamManager = DynamicServiceLoader.getInstance()
            .registerDynamicService(urlParamManagerDynamicConfig);
        this.urlParamManager.onDidInit((urlParamManager) => {
            urlParamManager.setServiceDelegates({
                extensionService: this.extensionService,
            });
        });

        this.vipInfoService = new VipInfoService();
        this.printService = new PrintService(this.createPrintServiceInjectable());
        this.printService.setServiceDelegates({
            uiDataService,
            linkManager: this.linkManager,
        });
        this.screenshotManager = new ScreenshotManager();
        this.screenshotManager.setUiDataService(uiDataService);

        this.anonymousViewService = DynamicServiceLoader.getInstance()
            .registerDynamicService(anonymousViewConfig);
        this.anonymousViewService.onDidInit((anonymousViewService) => {
            anonymousViewService.setServiceDelegates({
                userPrivilegeService: this.userPriviledgeService,
                rightSidebarService: this.rightSideBarService,
            });
        });
        this.tipsManager.setAnonymousViewServiceDelegate(this.anonymousViewService);
    }

    protected createApplyAuthInjectable(): IApplyAuthInjectable {
        return new ApplyAuthInjectable();
    }

    protected createPrintServiceInjectable(): IPrintServiceInjectable {
        return new PrintServiceInjectable();
    }

    protected shouldInitOpenClientApi() {
        return isPreview() || ENV.IS_LOCAL_EDIT;
    }

    protected initRegisterManager() {}
}
